import type { MarketingIndustrySlug } from './industry-page-model';
import type { MarketingIndustryPageSource } from './industry-page-content.types';

type FirstIndustrySlug = Extract<MarketingIndustrySlug, 'apparel' | 'footwear'>;

export const MARKETING_INDUSTRY_PAGE_CONTENT_A = {
  apparel: {
    heroBody:
      'Move every apparel style from brief to market without losing intent, time, or margin.',
    categories: [
      'Tops',
      'Bottoms',
      'Denim',
      'Dresses',
      'Knitwear',
      'Outerwear',
      'Activewear',
      'Intimates',
    ],
    benefitsHeading: 'Move faster without losing product truth',
    benefits: [
      {
        title: 'Reduce avoidable development loops',
        body: 'Resolve visible product decisions before physical sampling and give every team the approved version.',
      },
      {
        title: 'Protect cost and margin',
        body: 'Compare material, supplier, MOQ, and construction choices against the commercial plan.',
      },
      {
        title: 'Launch earlier and more accurately',
        body: 'Create product pages, imagery, and video from the approved garment before inventory arrives.',
      },
    ],
    workflowHeading: 'Follow one style—not six files',
    workflowSteps: [
      {
        number: '01',
        title: 'Frame the Brief',
        body: 'Define the customer, market, season, occasion, price point, and commercial target.',
        output: 'Product and collection brief',
      },
      {
        number: '02',
        title: 'Build the Garment',
        body: 'Explore silhouette, proportion, construction, material, print, color, and styling.',
        output: 'Concepts, visuals, colorways, and material options',
      },
      {
        number: '03',
        title: 'Make It Buildable',
        body: 'Turn approved intent into flats, measurements, tolerances, BOMs, labels, and tech packs.',
        output: 'Factory-ready technical product record',
      },
      {
        number: '04',
        title: 'Make It Sellable',
        body: 'Carry the approved garment into wholesale, retail, e-commerce, marketplaces, and campaigns.',
        output: 'Channel-ready launch content',
      },
    ],
    capabilitiesHeading: 'Built for apparel complexity',
    capabilities: [
      {
        title: 'Styles and variants',
        body: 'Keep styles, colorways, sizes, SKUs, materials, prints, trims, and finishes connected.',
      },
      {
        title: 'Fit and construction',
        body: 'Organize measurements, grading, tolerances, BOMs, construction, care, and packaging.',
      },
      {
        title: 'Commercial readiness',
        body: 'Connect cost, supplier, MOQ, lead time, and production requirements to the garment.',
      },
      {
        title: 'Product storytelling',
        body: 'Reuse approved product images, descriptions, listings, and traceability information.',
      },
    ],
    systemsHeading: 'Keep your existing product systems',
    systemsBody:
      'Connect IMAI with PLM, ERP, PIM, DAM, CAD, and commerce platforms. Your existing systems can remain the official record.',
    recordHeading: 'Or make My Drive the complete product record',
    recordBody:
      'Keep research, concepts, measurements, BOMs, samples, factory files, approvals, product data, and launch assets organized by season, collection, category, and style.',
    faq: {
      label: 'Frequently Asked Questions',
      heading: 'FAQ',
      items: [
        {
          question: 'Does IMAI replace our PLM?',
          answer:
            'It can work with your existing PLM, ERP, PIM, DAM, CAD, and commerce platforms, or My Drive can become the complete record from concept through launch.',
        },
        {
          question: 'Can IMAI eliminate physical samples?',
          answer:
            'It can reduce avoidable rounds by resolving decisions digitally. Physical fit, material, workmanship, testing, and production approval remain necessary where required.',
        },
        {
          question: 'How does IMAI handle fit and sizing?',
          answer:
            'IMAI organizes measurements, points of measure, tolerances, grading, sample comments, and approved specifications for qualified teams to review.',
        },
        {
          question: 'Can it manage sizes, colors, and SKUs?',
          answer:
            'Yes. Each style can remain connected to its colorways, materials, sizes, SKUs, specifications, images, and customer-facing information.',
        },
      ],
    },
    cta: {
      eyebrow: 'One connected apparel workflow',
      heading: 'Stop rebuilding the same garment.',
      cards: [
        { label: 'Build Your Apparel Workflow', href: '/auth?mode=signup' },
        { label: 'Discuss Your Apparel Workflow', href: '/contact' },
      ],
    },
  },
  footwear: {
    heroBody:
      'Resolve every component before the first prototype—from brief and colorway to BOM, development pack, and launch.',
    categories: [
      'Uppers',
      'Soles',
      'Linings',
      'Closures',
      'Hardware',
      'Colorways',
    ],
    benefitsHeading: 'Move faster before prototyping',
    benefits: [
      {
        title: 'Fewer prototype rounds',
        body: 'Resolve visible design, component, material, and color decisions digitally first.',
      },
      {
        title: 'Component-level control',
        body: 'Keep uppers, soles, linings, trims, colorways, and BOMs connected.',
      },
      {
        title: 'Keep your existing tools',
        body: 'Work alongside footwear CAD and PLM without rebuilding specialist engineering workflows.',
      },
    ],
    workflowHeading: 'Follow one shoe—not dozens of files',
    workflowSteps: [
      {
        number: '01',
        title: 'Define the Product',
        body: 'Set the customer, use case, category, price point, performance requirements, and delivery target.',
        output: 'Footwear brief and product architecture',
      },
      {
        number: '02',
        title: 'Design the Shoe',
        body: 'Develop the upper, sole direction, component layout, branding, color blocking, materials, and finishes.',
        output: 'Product views, colorways, and material options',
      },
      {
        number: '03',
        title: 'Make It Development-Ready',
        body: 'Connect the design to lasts, size runs, components, construction, BOMs, costing, and prototype needs.',
        output: 'Component maps, drawings, BOMs, and development packs',
      },
      {
        number: '04',
        title: 'Prepare the Factory Handoff',
        body: 'Confirm CAD references, testing, labels, packaging, tooling requirements, and approvals.',
        output: 'Factory-ready product pack',
      },
      {
        number: '05',
        title: 'Make It Sellable',
        body: 'Carry the approved shoe into product imagery, line sheets, product pages, video, and campaigns.',
        output: 'Channel-ready launch assets',
      },
    ],
    capabilitiesHeading: 'Everything connected to the shoe',
    capabilities: [
      {
        title: 'Product',
        body: 'Concepts, components, colorways, materials, finishes, lasts, sizes, and widths.',
      },
      {
        title: 'Development',
        body: 'Technical drawings, specifications, BOMs, costing, prototypes, testing, and approvals.',
      },
      {
        title: 'Production',
        body: 'Suppliers, CAD references, tooling information, factory files, labels, and packaging.',
      },
      {
        title: 'Launch',
        body: 'Product information, imagery, video, product pages, line sheets, and campaign assets.',
      },
    ],
    systemsHeading: 'Keep your existing footwear systems',
    systemsBody:
      'Connect footwear CAD, PLM, ERP, PIM, DAM, material libraries, and commerce platforms while specialist tools continue to own lasts, patterns, soles, molds, and tooling.',
    recordHeading: 'Or make My Drive the complete product record',
    recordBody:
      'Keep references, components, materials, size runs, BOMs, prototypes, tests, approvals, factory files, and launch assets together by season, collection, category, platform, and style.',
    faq: {
      label: 'Frequently Asked Questions',
      heading: 'FAQ',
      items: [
        {
          question: 'Does IMAI replace footwear CAD or PLM?',
          answer:
            'No. IMAI works with existing systems while specialist tools continue to handle lasts, patterns, soles, molds, and production tooling.',
        },
        {
          question: 'Can IMAI manage lasts and size runs?',
          answer:
            'IMAI can connect last references, size runs, widths, components, specifications, and approved decisions for qualified footwear teams to review.',
        },
        {
          question: 'Can IMAI eliminate physical prototypes?',
          answer:
            'It can reduce avoidable rounds. Physical fit, comfort, wear, performance, durability, and production approval remain necessary.',
        },
        {
          question: 'Can approved footwear become launch content?',
          answer:
            'Yes. Approved footwear can become imagery, detail views, product pages, line sheets, videos, and channel-specific assets.',
        },
      ],
    },
    cta: {
      eyebrow: 'One shoe. Every decision connected.',
      heading: 'Resolve more before the first prototype.',
      cards: [
        { label: 'Build a Footwear Concept', href: '/auth?mode=signup' },
        { label: 'Book a Footwear Demo', href: '/contact' },
      ],
    },
  },
} as const satisfies Record<FirstIndustrySlug, MarketingIndustryPageSource>;
